import React, { useState } from 'react';
import gcLogo from '../assets/gclogo.png';
import { PlusIcon, ArrowLeftOnRectangleIcon, ChevronDownIcon, ChatBubbleLeftRightIcon, StarIcon, EllipsisVerticalIcon } from '@heroicons/react/24/outline';

type HeaderProps = {
  variant?: 'login' | 'register' | 'onboarding';
  stepIndex?: number;
  totalSteps?: number;
  isLoggedIn?: boolean;
  userProfile?: {
    name?: string;
    role?: string;
    avatar?: string;
  };
  onLogout?: () => void;
  onShareInviteClick?: () => void;
  onRequestReviewClick?: () => void;
  onChatClick?: () => void;
  hasUnreadMessages?: boolean;
};

export function Header({
  variant,
  stepIndex = 0,
  totalSteps = 4,
  isLoggedIn = false,
  userProfile = {},
  onLogout,
  onShareInviteClick,
  onRequestReviewClick,
  onChatClick,
  hasUnreadMessages = false,
}: HeaderProps) {
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const [isMobileActionsOpen, setIsMobileActionsOpen] = useState(false);

  return (
    <header className="flex items-center justify-between px-4 sm:px-6 pt-7 pb-7 border-b border-[#2C2731] bg-[#2C2731] text-[#FEF6D6]">
      {/* Logo */}
      <div className="flex items-center gap-2 ml-2 sm:ml-12">
        <a href={(window as any).__ENV__?.SITE_URL || 'https://guidescollective.com'} className="h-10 flex items-center">
          <img src={gcLogo} alt="GuidesCollective Logo" className="h-10 w-auto" />
        </a>
      </div>

      {/* Content on the right side */}
      {isLoggedIn ? (
        <div className="flex items-center gap-4 mr-2 sm:mr-6">
          {/* Mobile actions dropdown */}
          <div className="relative sm:hidden">
            <button
              onClick={() => setIsMobileActionsOpen(!isMobileActionsOpen)}
              className="relative p-2 rounded-full transition-colors hover:bg-[#403851]"
              aria-label="Actions"
            >
              <EllipsisVerticalIcon className="h-6 w-6 text-[#FEF6D6]" />
              {hasUnreadMessages && (
                <span className="absolute top-1 right-1 w-2 h-2 bg-[#FEF6D6] rounded-full"></span>
              )}
            </button>

            {/* Mobile Actions Dropdown Menu */}
            {isMobileActionsOpen && (
              <div className="absolute right-0 mt-2 w-48 bg-white rounded-md shadow-lg ring-1 ring-black ring-opacity-5 z-50">
                <div className="py-1">
                  {onChatClick && (
                    <button
                      onClick={() => {
                        onChatClick();
                        setIsMobileActionsOpen(false);
                      }}
                      className="flex items-center gap-2 w-full px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 transition-colors"
                    >
                      <ChatBubbleLeftRightIcon className="h-4 w-4" />
                      Messages
                      {hasUnreadMessages && (
                        <span className="ml-auto w-2 h-2 bg-red-500 rounded-full"></span>
                      )}
                    </button>
                  )}
                  <button
                    onClick={() => {
                      if (onShareInviteClick) onShareInviteClick();
                      setIsMobileActionsOpen(false);
                    }}
                    className="flex items-center gap-2 w-full px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 transition-colors"
                  >
                    <PlusIcon className="h-4 w-4" />
                    Share Your Link
                  </button>
                  {onRequestReviewClick && (
                    <button
                      onClick={() => {
                        onRequestReviewClick();
                        setIsMobileActionsOpen(false);
                      }}
                      className="flex items-center gap-2 w-full px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 transition-colors"
                    >
                      <StarIcon className="h-4 w-4" />
                      Request Review
                    </button>
                  )}
                </div>
              </div>
            )}
          </div>

          {/* Desktop: Chat button */}
          {onChatClick && (
            <button
              onClick={onChatClick}
              className="hidden sm:block relative p-2 rounded-full transition-colors hover:bg-[#403851]"
              aria-label="Messages"
            >
              <ChatBubbleLeftRightIcon className="h-6 w-6 text-[#FEF6D6]" />
              {hasUnreadMessages && (
                <span className="absolute top-1 right-1 w-2 h-2 bg-[#FEF6D6] rounded-full"></span>
              )}
            </button>
          )}

          {/* Desktop: Share button */}
          <button
            onClick={onShareInviteClick}
            className="hidden sm:inline-flex items-center gap-2 h-9 px-4 rounded-lg bg-[#FEF6D6] text-[#2C2731] hover:bg-[#F2EAC3] shadow-sm"
          >
            <PlusIcon className="h-4 w-4" />
            <span className="text-sm font-medium">Share Your Link</span>
          </button>

          {/* Desktop: Request Review button */}
          {onRequestReviewClick && (
            <button
              onClick={onRequestReviewClick}
              className="hidden sm:inline-flex items-center gap-2 h-9 px-4 rounded-lg border border-[#FEF6D6] bg-transparent text-[#FEF6D6] hover:bg-[#FEF6D6]/10 shadow-sm"
            >
              <StarIcon className="h-4 w-4" />
              <span className="text-sm font-medium">Request Review</span>
            </button>
          )}

          {/* User info with dropdown */}
          <div className="relative">
            <button
              onClick={() => setIsDropdownOpen(!isDropdownOpen)}
              className="flex items-center gap-3 rounded-md p-2 transition-colors hover:bg-[#403851]"
            >
              <img
                src={userProfile.avatar || "/gclogo.png"}
                alt="User Avatar"
                className="h-9 w-9 rounded-full ring-1 ring-white/20 object-cover"
              />
              <div className="text-right leading-tight">
                <div className="text-sm font-medium text-white">{userProfile.name || 'User'}</div>
                <div className="text-xs text-gray-200">{userProfile.role || 'Member'}</div>
              </div>
              <ChevronDownIcon className="h-4 w-4 text-white" />
            </button>

            {/* Dropdown Menu */}
            {isDropdownOpen && (
              <div className="absolute right-0 mt-2 w-48 bg-white rounded-md shadow-lg ring-1 ring-black ring-opacity-5 z-50">
                <div className="py-1">
                  <button
                    onClick={() => {
                      if (onLogout) {
                        onLogout();
                      }
                      setIsDropdownOpen(false);
                    }}
                    className="flex items-center gap-2 w-full px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 transition-colors"
                  >
                    <ArrowLeftOnRectangleIcon className="h-4 w-4" />
                    Sign Out
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>
      ) : variant === 'login' ? (
        <div className="flex items-center gap-2 sm:gap-3 mr-2 sm:mr-6">
          <a
            href="/login"
            className="inline-flex items-center justify-center gap-2 px-3 sm:px-4 py-2 rounded-lg border border-[#FEF6D6] bg-transparent text-[#FEF6D6] text-sm font-medium transition-colors hover:bg-[#FEF6D6]/10 whitespace-nowrap"
          >
            Log In
          </a>
          <a
            href="/register"
            className="inline-flex items-center justify-center gap-2 px-3 sm:px-4 py-2 rounded-lg border border-[#FEF6D6] bg-[#FEF6D6] text-[#2C2731] text-sm font-medium transition-colors hover:bg-[#E8DFBD] whitespace-nowrap"
          >
            Sign Up
          </a>
        </div>
      ) : variant === 'register' ? (
        <div className="flex items-center gap-2 sm:gap-3 mr-2 sm:mr-6">
          <a
            href="/login"
            className="inline-flex items-center justify-center gap-2 px-3 sm:px-4 py-2 rounded-lg border border-[#FEF6D6] bg-transparent text-[#FEF6D6] text-sm font-medium transition-colors hover:bg-[#FEF6D6]/10 whitespace-nowrap"
          >
            Log In
          </a>
          <a
            href="/register"
            className="inline-flex items-center justify-center gap-2 px-3 sm:px-4 py-2 rounded-lg border border-[#FEF6D6] bg-[#FEF6D6] text-[#2C2731] text-sm font-medium transition-colors hover:bg-[#E8DFBD] whitespace-nowrap"
          >
            Sign Up
          </a>
        </div>
      ) : (
        <div className="text-sm text-[#FEF6D6] flex items-center gap-4 mr-2 sm:mr-12">
          <a href="mailto:support@guidescollective.com" className="hover:underline">
            Contact Support
          </a>
          {onLogout && (
            <button
              onClick={onLogout}
              className="inline-flex items-center gap-1 text-white hover:underline"
            >
              <ArrowLeftOnRectangleIcon className="h-4 w-4" />
              Logout
            </button>
          )}
        </div>
      )}
    </header>
  );
}
